import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {PageGlobalParameters} from '@models/pages/page-global-parameters';
import {PagesHelperService} from '@services/pages/pages-helper.service';
import {UserDefinedPageService} from '@services/pages/user-defined-page/user-defined-page.service';
import {UserDefinedPage} from '@models/sidebar/request/user-defined-page';
import {WidgetIdList} from '@models/pages/user-defined-page/widget-id-list';
import {UserDefinedWidget} from '@models/pages/user-defined-page/user-defined-widget';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {MachineService} from '@services/administration/machine/machine.service';
import {Machine} from '@models/machine/machine';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '@services/notification/notification.service';
import {UserDefinedTabSettingsRequest} from '@models/sidebar/request/user-defined-tab-settings-request';
import {UserPageGridLayoutItem} from '@models/pages/user-page-grid-layout-item';
import {WidgetDetails} from '@models/pages/user-defined-page/widget-details';
import {ChartSignalEntry} from '@models/pages/user-defined-page/chart-signal-entry';
import {MenuItemType} from '@models/sidebar/menu-item-type';
import {LeftMenuService} from '@services/left-menu/left-menu.service';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {ConfirmationDialogComponent} from '@shared/confirmation-dialog/confirmation-dialog.component';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {cloneDeep} from 'lodash';
import {MenuItem} from '@models/sidebar/menu-item';
import {BreadcrumbService} from '@services/breadcrumb/breadcrumb.service';
import {
  CloneUserDefinedPageComponent
} from '@components/pages/page-container/user-defined-page-container/modals/clone-user-defined-page/clone-user-defined-page.component';
import {User} from '@models/user/user';
import {TemplatePagesService} from '@services/left-menu/page/template-user-defined-page/template-pages.service';
import {Observable, Subject} from 'rxjs';
import {TemplatePageConfiguration} from '@models/sidebar/request/template-page-configuration';
import {takeUntil} from 'rxjs/operators';
import {Signal} from '@models/dashboard/signal';
import {MODAL_WITHOUT_BACKDROP_OPTION} from '@constants/modal-options/modal-options';
import {MenuNotification, OperationType} from '@models/sidebar/menu-notification';
import {
  FavoriteNotificationService
} from '@services/left-menu/favorites/favorite-notification/favorite-notification.service';
import {ModalService} from '@services/modal/modal.service';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';
import {Role} from '@models/user/role';
import {SettingsService} from '@services/administration/settings/settings.service';
import {btou} from '@prototypes/global.functions';

@Component({
    selector: 'scitis-user-defined-page-container',
    templateUrl: './user-defined-page-container.component.html',
    styleUrls: ['./user-defined-page-container.component.scss'],
    standalone: false
})
export class UserDefinedPageContainerComponent implements OnInit, OnDestroy {

  public pageGlobalParameters: PageGlobalParameters = new PageGlobalParameters();
  public isUserPageInEditMode: boolean = false;
  public userPageAddWidget: number = -1;
  public widgets: UserDefinedWidget[] = [];
  public initialWidgets: UserDefinedWidget[] = [];
  public machine: Machine = new Machine();
  public fallBackMessage = '';
  public isLoading: boolean = true;
  public routes: MenuItem[] = [];
  public user: User | undefined;
  public userDefinedPage: UserDefinedPage = new UserDefinedPage();
  public isTemplatePage: boolean = false;
  public isAdminOrHigher: boolean = false;
  public isAdminOrLower: boolean = false;
  public syncTemplatePage: boolean = false;
  @ViewChild('syncTemplate') syncTemplatePageCheckbox: ElementRef | undefined;
  public isDistributorOrViewerRole: boolean = false;
  public liveDataStreamingEnabled: boolean = true;

  private CURRENT_PAGE_NAME = 'UserDefinedPageContainerComponent';
  private layout: UserPageGridLayoutItem[] = [];
  private saveSuccessMessage = '';
  private saveErrorMessage = '';
  private errorGettingConfigurationMessage = '';
  private userDefinedPageBackup: UserDefinedPage = new UserDefinedPage();
  private copyStartedSuccessMessage = '';
  private copyStartedErrorMessage = '';
  private $destroy: Subject<boolean> = new Subject<boolean>();
  public get isDisabledEditButton(): boolean {
    return (!this.isPageOwner && !this.isTemplatePage && !this.isAdminOrHigher) || this.isLoading || this.isDistributorOrViewerRole || (this.isTemplatePage && this.isAdminOrLower);
  };

  constructor(private activatedRoute: ActivatedRoute,
              private pagesHelperService: PagesHelperService,
              private userDefinedPageService: UserDefinedPageService,
              private machineService: MachineService,
              private translate: TranslateService,
              private notification: NotificationService,
              private loadingIndicatorService: LoadingIndicatorService,
              private leftMenuService: LeftMenuService,
              private menuNotificationService: MenuNotificationService,
              private favoriteNotificationService: FavoriteNotificationService,
              private modalService: ModalService,
              private firebaseAuthenticationService: FirebaseAuthenticationService,
              private breadcrumbService: BreadcrumbService,
              private settingsService: SettingsService,
              private templatePagesService: TemplatePagesService) {
    this.setTranslations();
  }

  get displayEmptyStateFallback(): boolean {
    return this.widgets.length === 0;
  }

  get isPageOwner(): boolean {
    return this.userDefinedPage.userTabId.includes(String(this.user?.firebaseUid));
  }

  ngOnInit(): void {
    this.loadingIndicatorService.show();
    this.isLiveDataStreamingEnabled();
    this.determineIfTemplatePage();
    this.pageGlobalParameters = this.pagesHelperService.getPageDetailsFromUrl(this.activatedRoute.snapshot.paramMap);
    this.generateRoutes(this.pageGlobalParameters.associatedMenuItem);
    this.getPage(this.isTemplatePage);
    this.getMachineDetails(this.pageGlobalParameters);
    this.firebaseAuthenticationService.getLoggedUserDetails().pipe(takeUntil(this.$destroy)).subscribe((result: User) => {
      this.user = result;
      const userRole: Role = new Role(result);
      this.isDistributorOrViewerRole = (userRole.isDistributorRole || userRole.isViewerRole);
      this.isAdminOrHigher = userRole.isSuperAdminRole || userRole.isTechAdminRole || userRole.isAdminRole;
      this.isAdminOrLower = userRole.isAdminRole || userRole.isDistributorRole || userRole.isAnalyticsRole || userRole.isUserRole || userRole.isViewerRole;
    });
  }

  ngOnDestroy(): void {
    this.$destroy.next(true);
    this.$destroy.complete();
  }

  onEditLayoutChangeEvent() {
    this.userDefinedPageService.onEditLayout$.next(true);
    if (this.isUserPageInEditMode) {
      this.showConfirmationForUserPageCancelEdit();
    } else {
      if (this.isTemplatePage && this.syncTemplatePage) {
        this.showConfirmationForUserTemplatePageStartEdit();
      } else {
        this.isUserPageInEditMode = true;
      }
    }

  }

  onAddWidgetEvent() {
    this.userPageAddWidget++;
  }

  onSavePage() {
    if (!this.machine.id) {
      return;
    }
    const request: UserDefinedTabSettingsRequest = this.getUserDefinedTabSettingsRequest();
    this.loadingIndicatorService.show();
    this.isUserPageInEditMode = false;
    let observable: Observable<any>;
    if (this.isTemplatePage) {
      observable = this.templatePagesService.saveWidgets(this.machine.id, request.tabDetails.tabId, request.widgets);
    } else {
      observable = this.userDefinedPageService.savePage(this.pageGlobalParameters.firebaseUserId, String(this.machine.id), request);
    }
    observable
    .pipe(takeUntil(this.$destroy))
    .subscribe({
      next: () => {
        this.getPage(this.isTemplatePage);
        const message = this.saveSuccessMessage.replace('%s', this.pageGlobalParameters.pageName.toBold());
        this.notification.success(message, this.CURRENT_PAGE_NAME, true);
        this.userDefinedPageService.saveSuccess$.next(true);
        this.loadingIndicatorService.hide();
      }, error: error => {
        console.log(error);
        const message = this.saveErrorMessage.replace('%s', this.pageGlobalParameters.pageName.toBold());
        this.notification.error(message, error, this.CURRENT_PAGE_NAME, true);
        this.loadingIndicatorService.hide();
      }
    });

  }

  onPageDuplicateEvent() {
    const modalRef = this.modalService.open(CloneUserDefinedPageComponent, {size: 'lg'});
    modalRef.componentInstance.pageName = this.pageGlobalParameters.pageName;
    modalRef.componentInstance.organizationId = this.pageGlobalParameters.organizationId;
    modalRef.componentInstance.isAdminOrHigher = this.isAdminOrHigher;
    modalRef.result.then((result) => {
      this.loadingIndicatorService.show();
      this.userDefinedPageService.clonePage(this.pageGlobalParameters.pageId, this.pageGlobalParameters.firebaseUserId,
          this.pageGlobalParameters.machineId, result.targetMachineIds, result.sharedPage, this.isTemplatePage)
      .pipe(takeUntil(this.$destroy))
      .subscribe({
        next: () => {
          this.leftMenuService.resetCache();
          this.loadingIndicatorService.hide();
          this.notification.success(this.copyStartedSuccessMessage, this.CURRENT_PAGE_NAME, true);
        }, error: error => {
          this.loadingIndicatorService.hide();
          this.notification.error(`${this.copyStartedErrorMessage} ${this.pageGlobalParameters.pageName}.`, error, this.CURRENT_PAGE_NAME, true);
        }
      });
    });
  }

  getPage(isTemplatePage: boolean) {
    let observable: Observable<any>;
    if (isTemplatePage) {
      observable = this.templatePagesService.getPageDetails(this.pageGlobalParameters.machineId, this.pageGlobalParameters.pageId, false);
    } else {
      observable = this.userDefinedPageService.getPage(this.pageGlobalParameters.machineId, this.pageGlobalParameters.firebaseUserId, this.pageGlobalParameters.pageId);
    }

    observable.pipe(takeUntil(this.$destroy)).subscribe({
      next: (page: UserDefinedPage | TemplatePageConfiguration) => {
        this.parsePageAndGetWidgets(page, isTemplatePage);
      }, error: (error) => {
        console.log(error);
        this.isLoading = false;
        this.loadingIndicatorService.hide();
      }
    });
  }

  onLayoutUpdate(layout: UserPageGridLayoutItem[]): void {
    this.layout = layout;
  }

  onNewWidget(newWidget: UserDefinedWidget): void {
    this.widgets.push(newWidget);
  }

  onRemoveWidget([chartId, chartName]: [string, string]): void {
    this.widgets = this.widgets.filter(m =>
        String(m.id) !== chartId &&
        !(m.id === -1 && m.widgetDetails.widgetName === chartName)
    );
  }

  public onMoveToInteractiveDataAnalysis(event: ChartSignalEntry[]) {
    const selectedStringSignals: ChartSignalEntry[] = [];
    for (const signal of event) {
      let foundMachineSignal: Signal | undefined = undefined;
      if (this.isTemplatePage) {
        foundMachineSignal = this.findMachineSignalByName(signal.signalName);
      } else {
        foundMachineSignal = this.findMachineSignalById(signal.signalId);
      }
      if (foundMachineSignal) {
        signal.signalName = foundMachineSignal.table + '@' + foundMachineSignal.name;
      }
    }

    const parameters = {selectedSignals: event, selectedStringSignals: selectedStringSignals};
    this.focusOnLeftMenu();
    this.leftMenuService.navigateToPage(this.getRedirectParameter(parameters));
  }

  public onSyncTemplatePage() {
    if (!this.syncTemplatePageCheckbox) {
      return;
    }
    this.syncTemplatePageCheckbox.nativeElement.checked = this.syncTemplatePage;
    const machineTag = this.machine.machineTag ? this.machine.machineTag.toBold() : this.machine.machineTag;
    const messageTitle = this.translate.instant('Enable/Disable synchronization');
    let messageBody = this.translate.instant('Enabling user template page synchronization will make this page available to all machines of tag #tag and category #category. Are you sure you want to proceed?')
    .replace('#tag', machineTag).replace('#category', this.machine.category.toBold());
    if (this.syncTemplatePage) {
      messageBody = this.translate.instant('Disabling user template page synchronization will make this page available only to the parent machine of #tag and category #category. Are you sure you want to proceed?')
      .replace('#tag', machineTag).replace('#category', this.machine.category.toBold());
    }


    const messageOK = this.translate.instant('Yes');
    const messageCancel = this.translate.instant('Cancel');
    const modalRef = this.modalService.open(ConfirmationDialogComponent);
    modalRef.componentInstance.title = messageTitle;
    modalRef.componentInstance.body = messageBody;
    modalRef.componentInstance.okButtonText = messageOK;
    modalRef.componentInstance.cancelButtonText = messageCancel;

    modalRef.result.then(() => {
    }, (reason) => {
      if (reason === ModalDialogDismissOption.OK) {
        this.syncTemplatePage = !this.syncTemplatePage;
        this.setSynchronizationFlag();
      }
    });
  }

  private showConfirmationForUserTemplatePageStartEdit() {
    //entering edit mode for template page show warning;
    const messageTitle = this.translate.instant('Confirmation');
    const machineTag = this.machine.machineTag ? this.machine.machineTag.toBold() : this.machine.machineTag;
    const messageBody = this.translate.instant('Changes done to this page will be reflected on all machines with the #tag tag and #category category. Do you want to continue?')
    .replace('#tag', machineTag).replace('#category', this.machine.category.toBold());
    const messageOK = this.translate.instant('Yes');
    const messageCancel = this.translate.instant('No');
    const modalRef = this.modalService.open(ConfirmationDialogComponent, MODAL_WITHOUT_BACKDROP_OPTION);
    modalRef.componentInstance.title = messageTitle;
    modalRef.componentInstance.body = messageBody;
    modalRef.componentInstance.cancelButtonText = messageCancel;
    modalRef.componentInstance.okButtonText = messageOK;
    modalRef.result.then(() => {
    }, (reason) => {
      this.isUserPageInEditMode = reason !== ModalDialogDismissOption.CANCEL && reason !== ModalDialogDismissOption.CLOSE;
    });
  }

  private showConfirmationForUserPageCancelEdit() {

    const cancelMessageTitle = this.translate.instant('Cancel');
    let cancelMessageBody = this.translate.instant('Do you really want to cancel editing the user defined page %s?').replace('%s', this.pageGlobalParameters.pageName.toBold());
    if (this.isTemplatePage) {
      cancelMessageBody = this.translate.instant('Do you really want to cancel editing the user defined template page %s?').replace('%s', this.pageGlobalParameters.pageName.toBold());
    }
    const cancelMessageOK = this.translate.instant('Yes, cancel editing');
    const cancelMessageCancel = this.translate.instant('No, continue editing');
    const modalRef = this.modalService.open(ConfirmationDialogComponent, MODAL_WITHOUT_BACKDROP_OPTION);
    modalRef.componentInstance.title = cancelMessageTitle;
    modalRef.componentInstance.body = cancelMessageBody;
    modalRef.componentInstance.cancelButtonText = cancelMessageCancel;
    modalRef.componentInstance.okButtonText = cancelMessageOK;
    modalRef.result.then(() => {
    }, (reason) => {
      if (reason === ModalDialogDismissOption.CANCEL || reason === ModalDialogDismissOption.CLOSE) {
        this.isUserPageInEditMode = true;
      } else {
        this.userDefinedPage = cloneDeep(this.userDefinedPageBackup);
        if (this.userDefinedPage.widgets && this.userDefinedPage.widgets.length > 0) {
          this.getPageWidgets(this.userDefinedPage.widgets);
        }
        this.isUserPageInEditMode = false;
        this.widgets = [...this.initialWidgets];
        this.layout = [];
        this.initialWidgets.forEach(w => {
          const layoutItem = new UserPageGridLayoutItem(String(w.id), w.widgetDetails.topX, w.widgetDetails.topY, w.widgetDetails.width, w.widgetDetails.height, w.widgetDetails.widgetName, w.widgetDetails.widgetType);
          this.layout.push(layoutItem);
        });
      }
    });
  }

  private setSynchronizationFlag() {
    if (!this.machine.id) {
      return;
    }
    this.loadingIndicatorService.show();
    this.templatePagesService.setSynchronizationFlag(this.machine.id, this.pageGlobalParameters.pageId, this.syncTemplatePage)
    .pipe(takeUntil(this.$destroy))
    .subscribe({
      next: () => {
        const message = this.syncTemplatePage ? this.translate.instant('User defined template page synchronization enabled') : this.translate.instant('User defined template page synchronization disabled');
        this.notification.success(message, this.CURRENT_PAGE_NAME, false);
        this.loadingIndicatorService.hide();
        if (!this.syncTemplatePage) {
          this.menuNotificationService.broadcast(new MenuNotification(OperationType.UPDATE_BASE_MENU_ITEMS_ORDER, String(this.userDefinedPage.parentTabId), '', '', true));
          this.favoriteNotificationService.broadcast(new MenuNotification(OperationType.SHARE_OR_UN_SHARE_PAGE, String(this.userDefinedPage.tabId), '', '', true));
        }
      }, error: error => {
        this.notification.error(this.translate.instant('User defined template page synchronization changed failed.'), error, this.CURRENT_PAGE_NAME, true);
        this.loadingIndicatorService.hide();
      }
    });
  }

  private parsePageAndGetWidgets(page: UserDefinedPage | TemplatePageConfiguration, isTemplatePage: boolean): void {
    if (!page) {
      return;
    }

    let userDefinedPage: UserDefinedPage = page as UserDefinedPage;
    if (isTemplatePage || !userDefinedPage) {
      const templatePage = page as TemplatePageConfiguration;
      userDefinedPage = new UserDefinedPage();
      userDefinedPage.tabId = templatePage.id;
      userDefinedPage.tabName = templatePage.name;
      userDefinedPage.widgets = templatePage?.configurationDetails?.widgetIds;
      this.userDefinedPage = userDefinedPage;
      this.userDefinedPageBackup = cloneDeep(userDefinedPage);

      this.widgets = templatePage?.configurationDetails?.widgets;
      this.initialWidgets = [...templatePage?.configurationDetails?.widgets];
      this.syncTemplatePage = templatePage.synchronizationEnabled;
      this.isLoading = false;
      this.loadingIndicatorService.hide();
    } else {
      this.userDefinedPage = userDefinedPage;
      this.userDefinedPageBackup = cloneDeep(userDefinedPage);
      if (userDefinedPage.widgets && userDefinedPage.widgets.length > 0) {
        this.getPageWidgets(userDefinedPage.widgets);
      } else {
        this.isLoading = false;
        this.loadingIndicatorService.hide();
      }
    }
  }

  private getMachineDetails(pageGlobalParameters: PageGlobalParameters) {
    if (!pageGlobalParameters || !pageGlobalParameters.machineId || String(pageGlobalParameters.machineId).isNullOrWhitespace()) {
      this.notification.error(this.errorGettingConfigurationMessage, null, this.CURRENT_PAGE_NAME, true);
      return;
    }
    const machineId = Number(pageGlobalParameters.machineId);
    this.machineService.getMachineConfiguration(machineId)
    .pipe(takeUntil(this.$destroy))
    .subscribe({
      next: (machine: Machine) => {
        this.machine = new Machine(machine);
      }, error: error => {
        this.notification.error(this.errorGettingConfigurationMessage, error, this.CURRENT_PAGE_NAME, true);
      }
    });
  }

  private getPageWidgets(widgetIds: number[]) {
    const widgetIdList: WidgetIdList = new WidgetIdList();
    widgetIdList.widgetIds = widgetIds;
    this.userDefinedPageService.getPageWidgets(this.pageGlobalParameters.machineId, this.pageGlobalParameters.firebaseUserId, widgetIdList)
    .pipe(takeUntil(this.$destroy))
    .subscribe({
      next: (widgetsDefinition: UserDefinedWidget[]) => {
        this.widgets = widgetsDefinition;
        this.initialWidgets = [...widgetsDefinition];
        this.isLoading = false;
        this.loadingIndicatorService.hide();
      }, error: (error) => {
        console.log(error);
        this.isLoading = false;
        this.loadingIndicatorService.hide();
      }
    });
  }

  private getUserDefinedTabSettingsRequest(): UserDefinedTabSettingsRequest {
    const request: UserDefinedTabSettingsRequest = new UserDefinedTabSettingsRequest();
    request.tabDetails = this.userDefinedPage;
    request.tabDetails.widgets = [];
    request.widgets = [];
    for (const widget of this.widgets) {
      const layoutItem = this.layout.find(m => m.name === widget.widgetDetails.widgetName);

      if (layoutItem) {
        widget.widgetDetails = new WidgetDetails();
        widget.widgetDetails.widgetType = layoutItem.type;
        widget.widgetDetails.widgetName = layoutItem.name;
        widget.widgetDetails.topX = layoutItem.x;
        widget.widgetDetails.topY = layoutItem.y;
        widget.widgetDetails.width = layoutItem.w;
        widget.widgetDetails.height = layoutItem.h;
      }

      delete widget.userDefinedChartDetails.stringSignal;

      request.widgets.push(widget);
      request.tabDetails.widgets.push(widget.id);
    }
    return request;
  }


  private focusOnLeftMenu(): void {
    if (this.pageGlobalParameters.interactiveDataAnalysisMenuItem) {
      this.menuNotificationService.broadcastRedirectToInteractiveDataAnalysis(this.pageGlobalParameters.interactiveDataAnalysisMenuItem);
    }
  }

  private findMachineSignalById(signalId: number) {

    for (const signal in this.machine.signals) {
      if (this.machine.signals[signal].id === signalId) {
        return this.machine.signals[signal];
      }
    }
    return null;
  }

  private findMachineSignalByName(signalName: string) {

    for (const signal in this.machine.signals) {
      if (this.machine.signals[signal].name.toLowerCase() === signalName.toLowerCase()) {
        return this.machine.signals[signal];
      }
    }
    return null;
  }

  private getRedirectParameter(parameters) {
    const parameter = {
      type: MenuItemType.DATA_VISUALIZATION_PAGE,
      machine: this.machine.id,
      firebaseUserId: this.pageGlobalParameters.firebaseUserId,
      name: 'Interactive Data Analysis',
      pageId: this.pageGlobalParameters.interactiveDataAnalysisMenuItem?.pageId,
      organization: this.pageGlobalParameters.organizationId,
      machineCategory: this.pageGlobalParameters.machineCategory,
      widgetInformation: parameters,
      actualMenuItem: this.pageGlobalParameters.interactiveDataAnalysisMenuItem
    };

    const redirectParameters = btou(JSON.stringify(parameter));
    return ['interactiveDataAnalysis', redirectParameters];
  }

  private setTranslations(): void {
    this.errorGettingConfigurationMessage = this.translate.instant('Error getting machine configuration.');
    this.saveSuccessMessage = this.translate.instant('Settings for user defined tab %s saved successfully.');
    this.saveErrorMessage = this.translate.instant('Error saving Settings for user defined tab %s.');
    this.fallBackMessage = this.translate.instant('No widgets have been added yet. Go ahead and create one!');
    this.copyStartedSuccessMessage = this.translate.instant('Page copy process started with success');
    this.copyStartedErrorMessage = this.translate.instant('Error starting copying process for ');
  }

  private generateRoutes(menuItem: MenuItem) {
    this.routes = this.breadcrumbService.generateRoutes(menuItem);
  }

  private determineIfTemplatePage() {
    this.isTemplatePage = this.activatedRoute.snapshot.data?.isTemplate;
  }

  private isLiveDataStreamingEnabled() {
    this.settingsService.isLiveDataStreamingEnabled().subscribe({
      next: value => {
        this.liveDataStreamingEnabled = value;
      }
    });
  }
}
